import React, { useState, useEffect } from 'react'
import MyButton from './buttons'
import MyFixedTooltip from './MyFixedTooltip'
import { useTranslation } from 'react-i18next'
import {
  fetchAnnounceByUserId,
  incrementAnnounceViews,
  updateAnnounceUpdatedAt,
} from '../../services/announces'
import { GiUpgrade } from 'react-icons/gi'
import CookieManager from '../lib/CookiesManager'

interface BoostButtonProps {
  boostInfoTooltipContent: string
  tooltipId: string
}

const BoostButton: React.FC<BoostButtonProps> = ({}) => {
  const { t } = useTranslation()
  const [timeRemaining, setTimeRemaining] = useState<string>('')
  const [canBoost, setCanBoost] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [announce, setAnnounce] = useState<any>(null)
  const [intervalId] = useState<NodeJS.Timeout | null>(null)
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  const isValidDate = (date: any) => {
    const timestamp = Date.parse(date)
    return !isNaN(timestamp)
  }

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime()

      if (!isValidDate(announce?.boostedTime)) {
        setCanBoost(true)
        setTimeRemaining('')
        return
      }

      const secondsPassed = Math.floor(
        (now - new Date(announce?.boostedTime).getTime()) / 1000,
      )

      if (secondsPassed >= 86400) {
        setCanBoost(true)
        setTimeRemaining('')
        if (intervalId !== null) {
          clearInterval(intervalId)
        }
        return
      }

      const remainingSeconds = 86400 - secondsPassed
      const hoursRemaining = Math.floor(remainingSeconds / 3600)
      const minutesRemaining = Math.floor((remainingSeconds % 3600) / 60)
      const seconds = remainingSeconds % 60

      const formattedTime = `${hoursRemaining
        .toString()
        .padStart(2, '0')}:${minutesRemaining
        .toString()
        .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
      setTimeRemaining(formattedTime)
    }

    if (announce) {
      calculateTimeRemaining()
      const intervalId = setInterval(calculateTimeRemaining, 1000)
      return () => clearInterval(intervalId)
    }

    return () => {
      if (intervalId !== null) {
        clearInterval(intervalId)
      }
    }
  }, [announce, intervalId])

  useEffect(() => {
    const fetchUserAnnounce = async () => {
      const userId = CookieManager.getUserId()
      const token = CookieManager.getToken()

      if (userId && token) {
        try {
          const userAnnounce = await fetchAnnounceByUserId(userId, token)
          setAnnounce(userAnnounce)
          if (!isValidDate(userAnnounce?.boostedTime)) {
            setCanBoost(true)
            setTimeRemaining('')
          }
        } catch (error) {
          console.error('Error fetching user announce:', error)
        }
      }
    }

    fetchUserAnnounce()
  }, [])

  const handleBoost = async () => {
    event?.preventDefault()
    if (!canBoost || !announce) return

    setIsLoading(true)
    const userId = CookieManager.getUserId()
    const token = CookieManager.getToken()

    if (!userId || !token) {
      console.error('User ID or token is missing')
      setIsLoading(false)
      return
    }

    try {
      const updatedAnnounce = {
        ...announce,
        boostedTime: new Date().toISOString(),
      }

      const boostResult = await incrementAnnounceViews(
        userId,
        token,
        updatedAnnounce,
      )

      await updateAnnounceUpdatedAt(userId, token, {
        ...boostResult,
        boostedTime: updatedAnnounce.boostedTime,
      })

      const refreshedAnnounce = await fetchAnnounceByUserId(userId, token)
      setAnnounce(refreshedAnnounce)
      setCanBoost(false)
      console.log('Announce updated:', refreshedAnnounce)
    } catch (error) {
      if ((error as any).response?.data?.message) {
        if (
          error instanceof Error &&
          'response' in error &&
          (error as any).response?.data?.message
        ) {
          setErrorMessage((error as any).response.data.message.join(', '))
        } else {
          setErrorMessage("Une erreur est survenue lors du boost de l'annonce.")
        }
      } else {
        console.error('Error boosting announce:', error)
        setErrorMessage("Une erreur est survenue lors du boost de l'annonce.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect()
    setTooltipPosition({
      x: rect.left + rect.width / 2,
      y: rect.top,
    })
    setIsTooltipVisible(true)
  }

  const handleMouseLeave = () => {
    setIsTooltipVisible(false)
  }

  const boostInfoText = canBoost
    ? isLoading
      ? t('boosting')
      : t('boost-announce')
    : `${t('boost-available-in')} ${timeRemaining}`

  const tooltipText = 'boost-ad-tooltip'

  return (
    <div
      className='relative inline-block'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <MyButton
        variant={canBoost ? 'green' : 'redpink'}
        onClick={handleBoost}
        disabled={!canBoost || !announce || isLoading}
        className='mr-4 flex items-center'
      >
        <GiUpgrade className='mr-2' />
        <span>{boostInfoText}</span>
      </MyButton>
      {isTooltipVisible && (
        <MyFixedTooltip
          text={tooltipText}
          x={tooltipPosition.x}
          y={tooltipPosition.y}
        />
      )}
      {errorMessage && <div className='text-red-500 mt-2'>{errorMessage}</div>}
    </div>
  )
}

export default BoostButton
